import { useState, useRef, useMemo } from 'react';
import { api } from '../../api';
import Modal from '../../components/Modal';

// Bulk region change: download the property list → fill "New Region" for
// the ones moving → upload → review current → new → apply. Nothing is
// written until "Move N properties" is clicked.

const MUTED = '#6b7280';
const AMBER_BG = '#fffbeb';
const AMBER_BORDER = '#f59e0b';

function RegionPill({ region }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      background: '#eef2f7', color: '#1f2937', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>{region}</span>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, margin: '18px 0 8px' }}>
      {children}
    </div>
  );
}

const PROBLEM_LABEL = {
  unmatched: 'Not found in dispatch',
  bad_region: 'Region not recognized',
  conflict: 'Listed twice with different regions',
};

function problemText(r) {
  if (r.status === 'region_mismatch') {
    const where = r.property
      ? `dispatch has “${r.property.name}” in ${r.property.region}`
      : `every match in dispatch is outside ${r.sheet_current}`;
    return `Your sheet says it's in ${r.sheet_current} now, but ${where}`;
  }
  if (r.status === 'bad_region') return `${PROBLEM_LABEL.bad_region} (“${r.sheet_region}”)`;
  return PROBLEM_LABEL[r.status];
}

export default function RegionChangeModal({ onClose, onDone }) {
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [checked, setChecked] = useState({}); // sheet row # → bool
  const [picks, setPicks] = useState({});     // sheet row # → property id (ambiguous rows)
  const [moveCalls, setMoveCalls] = useState(true);
  const [showSame, setShowSame] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  async function handleDownload() {
    setError('');
    try {
      const blob = await api.downloadRegionChangeList();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'property-regions.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { setError('Could not download the list: ' + err.message); }
  }

  async function handleFile(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const data = await api.previewRegionChange(fd);
      const initial = {};
      for (const r of data.rows) {
        // Weak matches (partial name / address only) start unticked.
        if (r.status === 'change') initial[r.row] = !r.weak;
      }
      setPreview(data);
      setChecked(initial);
      setPicks({});
      setStep('preview');
    } catch (err) { setError(err.message); }
    setBusy(false);
  }

  const rowsBy = useMemo(() => {
    const out = { change: [], same: [], ambiguous: [], problems: [] };
    for (const r of preview?.rows || []) {
      if (r.status === 'change') out.change.push(r);
      else if (r.status === 'same' || r.status === 'duplicate') out.same.push(r);
      else if (r.status === 'ambiguous') out.ambiguous.push(r);
      else out.problems.push(r);
    }
    const key = r => `${r.property.region}→${r.new_region}|${r.property.name}`;
    out.change.sort((a, b) => key(a).localeCompare(key(b)));
    return out;
  }, [preview]);

  // What "Move" will actually send: ticked matches + ticked picks, one per property.
  const selected = useMemo(() => {
    const byId = new Map();
    for (const r of rowsBy.change) {
      if (checked[r.row]) byId.set(r.property.id, { id: r.property.id, region: r.new_region, from: r.property.region, open_calls: r.open_calls });
    }
    for (const r of rowsBy.ambiguous) {
      const cand = r.candidates.find(c => c.id === picks[r.row]);
      if (cand && checked[r.row] && cand.region !== r.new_region) {
        byId.set(cand.id, { id: cand.id, region: r.new_region, from: cand.region, open_calls: cand.open_calls });
      }
    }
    return [...byId.values()];
  }, [rowsBy, checked, picks]);

  const openCalls = selected.reduce((n, c) => n + (c.open_calls || 0), 0);

  async function handleApply() {
    setBusy(true);
    setError('');
    try {
      const res = await api.applyRegionChange(
        selected.map(({ id, region, from }) => ({ id, region, from })),
        moveCalls && openCalls > 0,
      );
      setResult(res);
      setStep('done');
      onDone();
    } catch (err) { setError(err.message); }
    setBusy(false);
  }

  function toggleAll(on) {
    const next = { ...checked };
    for (const r of rowsBy.change) next[r.row] = on;
    setChecked(next);
  }

  return (
    <Modal title="Change Property Regions" onClose={onClose} wide>
      {error && <div className="alert alert-error">{error}</div>}

      {step === 'upload' && (
        <div>
          <ol style={{ paddingLeft: 20, lineHeight: 1.8, margin: '0 0 16px' }}>
            <li>Download the property list.</li>
            <li>Type the new region (<b>Fort Worth</b>, <b>Dallas</b> or <b>Denton</b>) in the <b>New Region</b> column for each property that's moving. Leave the rest blank.</li>
            <li>Upload it here. You'll see every change before anything is saved.</li>
          </ol>
          <p style={{ color: MUTED, fontSize: 13, margin: '0 0 20px' }}>
            Your own spreadsheet works too — it just needs a <b>Property Name</b> column and a <b>New Region</b> column (Address helps match).
          </p>
          <div className="modal-actions">
            <button type="button" className="btn btn-muted" onClick={handleDownload}>Download Property List</button>
            <button type="button" className="btn btn-primary" onClick={() => fileRef.current.click()} disabled={busy}>
              {busy ? 'Reading…' : 'Upload Filled Sheet'}
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ display: 'none' }} />
          </div>
        </div>
      )}

      {step === 'preview' && preview && (
        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <span style={{ fontWeight: 600 }}>{preview.filename}</span>
            <span style={{ color: MUTED, fontSize: 13 }}>
              {rowsBy.change.length} moving · {rowsBy.same.length} already there
              {rowsBy.ambiguous.length > 0 && ` · ${rowsBy.ambiguous.length} need a pick`}
              {rowsBy.problems.length > 0 && ` · ${rowsBy.problems.length} not applied`}
            </span>
          </div>

          {rowsBy.change.length > 0 && (
            <>
              <SectionTitle>
                Moving
                <button type="button" className="btn-link" style={{ marginLeft: 10, fontSize: 12, textTransform: 'none', letterSpacing: 0 }} onClick={() => toggleAll(true)}>Select all</button>
                <button type="button" className="btn-link" style={{ marginLeft: 8, fontSize: 12, textTransform: 'none', letterSpacing: 0 }} onClick={() => toggleAll(false)}>None</button>
              </SectionTitle>
              <div className="table-wrapper" style={{ maxHeight: 340, overflowY: 'auto' }}>
                <table className="data-table">
                  <tbody>
                    {rowsBy.change.map(r => (
                      <tr key={r.row} style={r.weak ? { background: AMBER_BG } : undefined}>
                        <td style={{ width: 32 }}>
                          <input type="checkbox" checked={!!checked[r.row]} onChange={e => setChecked(c => ({ ...c, [r.row]: e.target.checked }))} />
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{r.property.name}</div>
                          <div style={{ color: MUTED, fontSize: 12 }}>{r.property.address}, {r.property.city}</div>
                          {r.weak && (
                            <div style={{ fontSize: 12, marginTop: 2, color: '#92400e' }}>
                              Check this match — your sheet says “{r.sheet_name || r.sheet_address}”
                            </div>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          <RegionPill region={r.property.region} /> <span style={{ color: MUTED }}>→</span> <RegionPill region={r.new_region} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {rowsBy.ambiguous.length > 0 && (
            <>
              <SectionTitle>Pick the right property</SectionTitle>
              {rowsBy.ambiguous.map(r => (
                <div key={r.row} style={{ border: `1px solid ${AMBER_BORDER}`, background: AMBER_BG, borderRadius: 8, padding: 10, marginBottom: 8 }}>
                  <div style={{ fontSize: 13, marginBottom: 6 }}>
                    Row {r.row}: <b>{r.sheet_name}</b>{r.sheet_address && ` — ${r.sheet_address}`} → <RegionPill region={r.new_region} />
                  </div>
                  <select
                    value={picks[r.row] || ''}
                    onChange={e => {
                      const id = parseInt(e.target.value, 10) || null;
                      setPicks(p => ({ ...p, [r.row]: id }));
                      setChecked(c => ({ ...c, [r.row]: !!id }));
                    }}
                    style={{ width: '100%' }}
                  >
                    <option value="">Skip this one</option>
                    {r.candidates.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {c.address}, {c.city} ({c.region}{c.is_active ? '' : ', inactive'})
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </>
          )}

          {rowsBy.problems.length > 0 && (
            <>
              <SectionTitle>Not applied — fix in the sheet or add the property first</SectionTitle>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7, maxHeight: 180, overflowY: 'auto' }}>
                {rowsBy.problems.map(r => (
                  <li key={r.row}>
                    Row {r.row}: <b>{r.sheet_name || r.sheet_address || '(blank)'}</b>
                    <span style={{ color: MUTED }}> — {problemText(r)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {rowsBy.same.length > 0 && (
            <>
              <SectionTitle>
                Already in that region ({rowsBy.same.length})
                <button type="button" className="btn-link" style={{ marginLeft: 10, fontSize: 12, textTransform: 'none', letterSpacing: 0 }} onClick={() => setShowSame(s => !s)}>
                  {showSame ? 'Hide' : 'Show'}
                </button>
              </SectionTitle>
              {showSame && (
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7, color: MUTED, maxHeight: 180, overflowY: 'auto' }}>
                  {rowsBy.same.map(r => <li key={r.row}>{r.property.name} — {r.property.region}</li>)}
                </ul>
              )}
            </>
          )}

          {openCalls > 0 && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 18, fontSize: 14 }}>
              <input type="checkbox" checked={moveCalls} onChange={e => setMoveCalls(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                Also move the <b>{openCalls}</b> unclaimed call{openCalls === 1 ? '' : 's'} at these properties to the new region
                <span style={{ color: MUTED }}> (open and scheduled — claimed calls stay with their driver)</span>
              </span>
            </label>
          )}

          <div className="modal-actions" style={{ marginTop: 20 }}>
            <button type="button" className="btn btn-muted" onClick={() => { setStep('upload'); setPreview(null); setError(''); }} disabled={busy}>
              Upload a Different File
            </button>
            <button type="button" className="btn btn-primary" onClick={handleApply} disabled={busy || selected.length === 0}>
              {busy ? 'Saving…' : `Move ${selected.length} Propert${selected.length === 1 ? 'y' : 'ies'}`}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && result && (
        <div>
          <div className="alert alert-success">
            Moved {result.updated} propert{result.updated === 1 ? 'y' : 'ies'} to {result.updated === 1 ? 'its' : 'their'} new region.
            {result.calls_moved > 0 && ` ${result.calls_moved} unclaimed call${result.calls_moved === 1 ? '' : 's'} moved with them.`}
          </div>
          {(result.unchanged > 0 || result.not_found > 0 || result.stale > 0) && (
            <p style={{ color: MUTED, fontSize: 13 }}>
              {result.unchanged > 0 && `${result.unchanged} were already in that region. `}
              {result.not_found > 0 && `${result.not_found} no longer exist. `}
              {result.stale > 0 && `${result.stale} changed region since the preview and were skipped.`}
            </p>
          )}
          <div className="modal-actions">
            <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
